import { Link } from 'react-router-dom';
import { MapPin, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import CTABand from '../components/CTABand';
import { Reveal } from '../components/motion';
import { IMAGES, CITIES, cityPath, localBusinessSchema, breadcrumbSchema } from '../data/site';

export default function ServiceAreas() {
  return (
    <>
      <SEO
        title="HVAC & Plumbing Service Areas | B.E. Holbrooks Co. Stanfield, NC"
        description="B.E. Holbrooks Co. serves Stanfield, Locust, Charlotte, Concord, Kannapolis, Albemarle, Norwood, Monroe, Gastonia, Huntersville, and surrounding NC communities. Call 704-888-2411."
        path="/service-areas"
        schemas={[
          localBusinessSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Service Areas', path: '/service-areas' }]),
        ]}
      />

      <PageHero
        eyebrow="Where We Work"
        h1Lines={['HVAC & Plumbing', 'Service Areas']}
        sub="From our home base at 204 West Stanly Street in Stanfield, B.E. Holbrooks Co. provides heating, air conditioning, plumbing, excavation, and trenching services across Stanly, Cabarrus, Mecklenburg, Union, and Gaston counties."
        image={IMAGES.rooftopAc}
        imageAlt="HVAC and plumbing service areas across the greater Charlotte NC region"
        crumbs={[{ name: 'Service Areas' }]}
      />

      <section className="bg-white py-16 lg:py-24" data-testid="service-areas-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              Communities We Serve Across North Carolina
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              For over 60 years, our family-owned company has served homeowners and businesses throughout the
              greater Charlotte region. Select your community below for localized HVAC and plumbing service details.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CITIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.1}>
                <Link
                  to={cityPath(c.slug)}
                  data-testid={`area-card-${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1.5 hover:border-brand hover:shadow-xl"
                >
                  <div className="flex items-center justify-between">
                    <span className="rounded-lg bg-brand-soft p-2.5 text-brand"><MapPin className="h-5 w-5" /></span>
                    <span className="font-mono text-xs font-bold uppercase tracking-widest text-slate-400">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-navy group-hover:text-brand">{c.name}, NC</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                    HVAC, heating, air conditioning, plumbing, excavation & trenching services in {c.name} and surrounding {c.name === 'Stanfield' ? 'Stanly County' : 'area'} communities.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-widest text-brand">
                    View local services <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 max-w-3xl">
            <p className="text-base leading-relaxed text-slate-600">
              Do not see your town listed? We serve many surrounding North Carolina communities beyond this list.
              Call us and ask — chances are our technicians are already working in your neighborhood.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand heading="Not Sure If We Serve Your Area?" testid="cta-service-areas" />
    </>
  );
}
